import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';
import { parsePhoneNumber } from 'awesome-phonenumber';
import isWebView from '$src/utils/user-agent';
import { Input } from '@amzn/ring-react-components/csr/components/Input';
import { PhoneInput } from '@amzn/ring-react-components/csr/components/PhoneInput';

import './index.less';

const COMPANY_NAME_CHAR_LIMIT = 50
const DEFAULT_REGION_CODE = "US"

const PRIVATE_GUARD_FORM_STRINGS = {
  COMPANY_NAME_LABEL: 'Security Company Name',
  COMPANY_NAME_PLACEHOLDER: 'Enter Security Company Name',
  PHONE_LABEL: 'Guard Phone Number',
  PHONE_PLACEHOLDER: 'Enter Number',
  PHONE_HELP_TEXT_PREFIX: 'To make any changes contact Ring support at ',
  PHONE_HELP_TEXT_EDITABLE: 'Ring\'s monitoring center will use this number to request dispatch from your security service.',
  PHONE_HELP_TEXT_EMAIL: 'monitoring@rs.ring.com',
  ERROR_REQUIRED: 'This field is required.',
  ERROR_CHAR_LIMIT: (limit) => `This field should be less than ${limit} characters.`,
  ERROR_SPECIAL_CHARS: 'Emojis are not supported. Please try again.',
  ERROR_PHONE_TOO_SHORT: 'Phone number is too short.',
  ERROR_PHONE_TOO_LONG: 'Phone number is too long.',
  ERROR_INVALID_COUNTRY_CODE: 'Invalid country code.',
  ERROR_INVALID_PHONE: 'Enter a valid phone number.'
};

function PrivateGuardForm({
  initialCompanyName = '',
  initialPhoneNumber = '',
  onChange,
  companyNameCharsLimit = COMPANY_NAME_CHAR_LIMIT,
  regionCode = DEFAULT_REGION_CODE,
  editableForm = true,
}) {
  const [formState, setFormState] = useState({
    companyName: initialCompanyName,
    phoneNumber: initialPhoneNumber,
    errors: {
      companyName: false,
      phoneNumber: false,
    }
  });

  const validateCompanyName = useCallback(
    (value) => {
      if (!value || value.length === 0 || value.trim() === '') {
        return PRIVATE_GUARD_FORM_STRINGS.ERROR_REQUIRED;
      }
      if (value.length > companyNameCharsLimit) {
        return PRIVATE_GUARD_FORM_STRINGS.ERROR_CHAR_LIMIT(companyNameCharsLimit);
      }
      // Check for emoji characters
      const emojiRegex = /[\u{1F300}-\u{1F5FF}\u{1F900}-\u{1F9FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
      if (emojiRegex.test(value)) {
        return PRIVATE_GUARD_FORM_STRINGS.ERROR_SPECIAL_CHARS;
      }
      
      return false;
    },
    [companyNameCharsLimit],
  );

  const validatePhoneNumber = useCallback((localPhone) => {
    if (!localPhone || localPhone.trim() === '') {
      return PRIVATE_GUARD_FORM_STRINGS.ERROR_REQUIRED;
    }
    const parsedNumber = parsePhoneNumber(localPhone, { regionCode });

    if (!parsedNumber.valid) {
      switch (parsedNumber.possibility) {
        case 'too-short':
          return PRIVATE_GUARD_FORM_STRINGS.ERROR_PHONE_TOO_SHORT;
        case 'too-long':
          return PRIVATE_GUARD_FORM_STRINGS.ERROR_PHONE_TOO_LONG;
        case 'invalid-country-code':
          return PRIVATE_GUARD_FORM_STRINGS.ERROR_INVALID_COUNTRY_CODE;
        default:
          return PRIVATE_GUARD_FORM_STRINGS.ERROR_INVALID_PHONE;
      }
    }

    return false;
  }, [regionCode]);

  const calculateFormValidity = useCallback((companyName, phoneNumber, companyError, phoneError) => companyName !== '' && phoneNumber !== '' && !companyError && !phoneError, []);

  const handleCompanyNameChange = useCallback(
    (event) => {
      const {value} = event.target;
      
      // Validate the input value without sanitizing
      const error = validateCompanyName(value);
      
      setFormState(prev => ({
        ...prev,
        companyName: value,
        errors: {
          ...prev.errors,
          companyName: error
        }
      }));

      if (onChange) {
        const isValid = calculateFormValidity(value, formState.phoneNumber, error, formState.errors.phoneNumber);
        onChange(value, formState.phoneNumber, isValid);
      }
    },
    [validateCompanyName, formState.phoneNumber, formState.errors.phoneNumber, onChange, calculateFormValidity],
  );

  const handlePhoneNumberChange = useCallback(
    (_, localPhone) => {
      const updatedPhone = localPhone || '';
      const error = validatePhoneNumber(updatedPhone);
      
      setFormState(prev => ({
        ...prev,
        phoneNumber: updatedPhone,
        errors: {
          ...prev.errors,
          phoneNumber: error
        }
      }));

      if (onChange) {
        const isValid = calculateFormValidity(formState.companyName, updatedPhone, formState.errors.companyName, error);
        onChange(formState.companyName, updatedPhone, isValid);
      }
    },
    [validatePhoneNumber, formState.companyName, formState.errors.companyName, onChange, calculateFormValidity],
  );

  return (
    <div className="private-guard__form" data-testid="private-guard-form">
      <Input
        label={PRIVATE_GUARD_FORM_STRINGS.COMPANY_NAME_LABEL}
        type="text"
        id="securityCompanyName"
        data-testid="security-company-name"
        disabled={!editableForm}
        value={formState.companyName}
        status={formState.errors.companyName ? 'error' : 'default'}
        className={`private-guard__form__input ${isWebView() ? 'avoid-autofocus' : ''} `}
        onChange={handleCompanyNameChange}
        placeholder={PRIVATE_GUARD_FORM_STRINGS.COMPANY_NAME_PLACEHOLDER}
      />
      <span className={`private-guard__form__message${formState.errors.companyName ? '--error' : ''}`}>
        {formState.errors.companyName ? formState.errors.companyName : `${formState.companyName?.length}/${companyNameCharsLimit}`}
      </span>
      <PhoneInput
        className={`private-guard__form__input ${isWebView() ? 'avoid-autofocus' : ''} `}
        alpha2Codes={['US']}
        closeOnClickOutside
        closeOnFocusOutside
        disabled={!editableForm}
        label={PRIVATE_GUARD_FORM_STRINGS.PHONE_LABEL}
        id="guardPhoneNumber"
        data-testid="guard-phone-number"
        onChangeCallback={handlePhoneNumberChange}
        status={formState.errors.phoneNumber ? 'error' : 'default'}
        value={formState.phoneNumber}
        placeholder={PRIVATE_GUARD_FORM_STRINGS.PHONE_PLACEHOLDER}
      />
<span className={`private-guard__form__message${formState.errors.phoneNumber ? '--error' : ''}`}>
  {formState.errors.phoneNumber ? (
    formState.errors.phoneNumber
  ) : (
    <>
      {editableForm ? (
        PRIVATE_GUARD_FORM_STRINGS.PHONE_HELP_TEXT_EDITABLE
      ) : (
        <>
          {PRIVATE_GUARD_FORM_STRINGS.PHONE_HELP_TEXT_PREFIX}
          <a href={`mailto:${PRIVATE_GUARD_FORM_STRINGS.PHONE_HELP_TEXT_EMAIL}`}>
            {PRIVATE_GUARD_FORM_STRINGS.PHONE_HELP_TEXT_EMAIL}
          </a>.
        </>
      )}
    </>
  )}
</span>
    </div>
  );
}

PrivateGuardForm.propTypes = {
  initialCompanyName: PropTypes.string,
  initialPhoneNumber: PropTypes.string,
  onChange: PropTypes.func,
  companyNameCharsLimit: PropTypes.number,
  regionCode: PropTypes.string,
  editableForm: PropTypes.bool,
};

export default observer(PrivateGuardForm);
